/**
 * Hooks de runtime operacional do Kiosk.
 *
 * Mantém a API pública `Hooks.*` intacta, mas retira do entrypoint
 * lógica de layout e sorting partilhada pelo Kiosk cloud e editor de menus.
 */

export const RfidHeightAdjust = {
  mounted() {
    this._adjust = this.adjust.bind(this);
    requestAnimationFrame(this._adjust);
    this._onResize = () => requestAnimationFrame(this._adjust);
    window.addEventListener('resize', this._onResize, { passive: true });

    try {
      this._ro = new ResizeObserver(() => this._adjust());
      this._ro.observe(this.el);
    } catch (_) { }
  },

  updated() {
    this._adjust();
  },

  destroyed() {
    try {
      window.removeEventListener('resize', this._onResize, { passive: true });
    } catch (_) { }

    try {
      this._ro && this._ro.disconnect && this._ro.disconnect();
    } catch (_) { }

    if (this.el) this.el.style.minHeight = '';
  },

  adjust() {
    try {
      const el = this.el;
      if (!el || !el.isConnected) return;

      if (window.innerHeight > window.innerWidth) {
        el.style.minHeight = '';
        delete el.dataset.baseHeight;
        return;
      }

      let base = parseInt(el.dataset.baseHeight || '0', 10);
      if (!base || Number.isNaN(base)) {
        const prev = el.style.minHeight;
        if (prev) el.style.minHeight = '';
        base = Math.max(el.scrollHeight, el.getBoundingClientRect().height | 0);
        if (prev) el.style.minHeight = prev;
        el.dataset.baseHeight = String(base);
      }

      if (base > 0) {
        const target = Math.round(base * 1.10);
        const current = parseInt((el.style.minHeight || '0').replace('px', ''), 10) || 0;
        if (Math.abs(current - target) > 2) {
          el.style.minHeight = `${target}px`;
        }
      }
    } catch (e) {
      console.warn('RfidHeightAdjust failed:', e);
    }
  }
};

export const MenuLevelsSortable = {
  mounted() {
    this.setup();
  },

  updated() {
    this.setup();
  },

  setup() {
    const container = this.el;
    const items = Array.from(container.querySelectorAll('[data-level-id]'));

    items.forEach((item) => {
      item.setAttribute('draggable', 'true');

      item.addEventListener('dragstart', (e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', item.dataset.levelId);
        item.classList.add('opacity-50');
      });

      item.addEventListener('dragend', () => {
        item.classList.remove('opacity-50');
      });

      item.addEventListener('dragover', (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      });

      item.addEventListener('drop', (e) => {
        e.preventDefault();
        const fromId = e.dataTransfer.getData('text/plain');
        const toId = item.dataset.levelId;
        if (!fromId || fromId === toId) return;

        const fromEl = container.querySelector(`[data-level-id="${fromId}"]`);
        container.insertBefore(fromEl, item);
        this.pushNewOrder();
      });
    });
  },

  pushNewOrder() {
    const order = Array.from(this.el.querySelectorAll('[data-level-id]'))
      .map((el) => parseInt(el.dataset.levelId, 10));

    this.pushEvent('reorder_levels', { order });
  }
};

export const KioskOpsRuntimeHooks = {
  RfidHeightAdjust,
  MenuLevelsSortable
};
